// Coleção de cosméticos (lote 3, item 15): no perfil, equipar a mesa, o verso e a moldura, ver o que falta e de onde
// cada item vem, e a loja com os itens à venda em Tentos (sem contagem regressiva, sem sorteio). Item liberado fica na
// conta e não volta a virar Tentos. Tudo validado no servidor; aqui só mostra.
import { useEffect, useState } from 'react';
import type { CategoriaCosmetico, Colecao, CosmeticoNaTela } from '@truco6/gamificacao';
import { imagemDaCarta } from '@truco6/ui';
import { comprarCosmetico, equiparCosmetico, lerColecao } from '../comum/api.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { PreviaDaMesa, versoDoCosmetico } from '../comum/cosmeticos.tsx';
import { ir, voltar } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { Tela, Topo } from '../telas/comum.tsx';
import { Icone } from '../telas/planos-comum.tsx';
import { Tento } from '../telas/tentos-comum.tsx';

type Aba = CategoriaCosmetico | 'loja';
const ABAS: { id: Aba; nome: string }[] = [{ id: 'mesa', nome: 'Mesa' }, { id: 'verso', nome: 'Verso' }, { id: 'moldura', nome: 'Moldura' }, { id: 'loja', nome: 'Loja' }];

export function Previa({ x, sessao, perfil, tamanho = 56 }: { x: { id: string; categoria: CategoriaCosmetico }; sessao: Sessao; perfil: PerfilConta; tamanho?: number }) {
  if (x.categoria === 'mesa') return <PreviaDaMesa id={x.id} tamanho={tamanho} />;
  if (x.categoria === 'verso') return <img className="previa-verso" src={imagemDaCarta('verso', versoDoCosmetico(x.id))} alt="" style={{ height: tamanho }} />;
  return <Rosto perfil={perfil} usuario={sessao.id} cor="var(--nos-400)" moldura={x.id} tamanho={Math.round(tamanho * 0.8)} />;
}

function Item({ x, sessao, perfil, ocupado, equipar, comprar }: {
  x: CosmeticoNaTela; sessao: Sessao; perfil: PerfilConta; ocupado: boolean; equipar: () => void; comprar: () => void;
}) {
  let acao: React.ReactNode;
  if (x.equipado) acao = <span className="etiqueta equipado">Equipado</span>;
  else if (x.tem) acao = <button className="btn2 pequeno" disabled={ocupado} onClick={equipar}>Equipar</button>;
  else if (x.aVenda && !sessao.anonimo) acao = <button className="btn-comprar" disabled={ocupado} onClick={comprar}><Tento tamanho={16} />{x.preco} Tentos</button>;
  else acao = <span className="bloqueado"><i aria-hidden="true">🔒</i>{x.como}</span>;
  return (
    <li className={'item-colecao' + (x.equipado ? ' on' : '') + (!x.tem ? ' falta' : '')}>
      <div className="previa"><Previa x={x} sessao={sessao} perfil={perfil} /></div>
      <b>{x.nome}</b>
      {x.tem && <small>{x.comprado ? 'Comprado' : x.como}</small>}
      {acao}
    </li>
  );
}

export function ColecaoTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [c, setC] = useState<Colecao | null>(null);
  const [aba, setAba] = useState<Aba>('mesa');
  const [confirmar, setConfirmar] = useState<CosmeticoNaTela | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    let vivo = true;
    lerColecao(sessao).then((x) => { if (vivo) setC(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  async function agir(fn: () => Promise<Colecao>, ok?: string) {
    setOcupado(true); setErro(null); setMsg(null);
    try { setC(await fn()); if (ok) setMsg(ok); } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); setConfirmar(null); }
  }
  const itens = c ? (aba === 'loja' ? c.itens.filter((x) => x.aVenda) : c.itens.filter((x) => x.categoria === aba)) : [];
  const meu = (cat: CategoriaCosmetico) => c?.itens.find((x) => x.id === c.equipados[cat]);
  return (
    <Tela larga>
      <Topo titulo="Coleção" voltar={voltar} />
      <div className="colecao">
        <div className="colecao-lado">
          {erro && <p className="erro" role="alert">{erro}</p>}
          {msg && <p className="ok" role="status">{msg}</p>}
          {!c && !erro && <p className="carregando" role="status">Um instante...</p>}
          {c && (
            <section className="cartao seu-visual" aria-label="Seu visual">
              <b className="rotulo dourado">SEU VISUAL</b>
              <div className="visual-atual">
                {(['mesa', 'verso', 'moldura'] as const).map((cat) => {
                  const x = meu(cat);
                  return x ? <div key={cat}><Previa x={x} sessao={sessao} perfil={perfil} tamanho={52} /><small>{x.nome}</small></div> : null;
                })}
              </div>
              {!sessao.anonimo && c.saldo !== null && (
                <button className="saldo-colecao" onClick={() => ir({ tela: 'loja' })}>
                  <Tento tamanho={20} /><span><b>{c.saldo}</b> Tentos disponíveis</span><span className="ver">Comprar Tentos</span>
                </button>
              )}
              <p className="nota">A moldura acompanha seu rosto em toda mesa. Na sala que você cria, todos jogam com a sua mesa e o seu verso.</p>
            </section>
          )}
        </div>
        {c && (
          <div className="colecao-lado">
            <div className="abas-colecao" role="tablist" aria-label="Categoria">
              {ABAS.map((a) => (
                <button key={a.id} role="tab" aria-selected={aba === a.id} className={aba === a.id ? 'on' : undefined} onClick={() => setAba(a.id)}>{a.nome}</button>
              ))}
            </div>
            {aba === 'loja' && <p className="nota">Item comprado fica na sua conta, mesmo se você trocar de plano.</p>}
            {itens.length ? (
              <ul className="grade-colecao" aria-label={ABAS.find((a) => a.id === aba)!.nome}>
                {itens.map((x) => (
                  <Item key={x.id} x={x} sessao={sessao} perfil={perfil} ocupado={ocupado}
                    equipar={() => agir(() => equiparCosmetico(sessao, x.id))} comprar={() => setConfirmar(x)} />
                ))}
              </ul>
            ) : <p className="nota">Você já tem todos os itens da loja.</p>}
          </div>
        )}
      </div>
      {confirmar && (
        <div className="fundo-confirmar" role="dialog" aria-label={`Comprar ${confirmar.nome}`}>
          <div className="cartao confirmar-compra">
            <div className="previa"><Previa x={confirmar} sessao={sessao} perfil={perfil} tamanho={72} /></div>
            <p><b>Comprar {confirmar.nome} por {confirmar.preco} Tentos?</b></p>
            <p className="nota">O item fica na sua conta e não volta a virar Tentos.</p>
            <button className="btn1 largo" disabled={ocupado} onClick={() => agir(() => comprarCosmetico(sessao, confirmar.id), `${confirmar.nome} agora é seu.`)}>COMPRAR</button>
            <button className="btn2 largo" disabled={ocupado} onClick={() => setConfirmar(null)}>Cancelar</button>
          </div>
        </div>
      )}
    </Tela>
  );
}

// No perfil: o atalho para a Coleção, com o visual de agora.
export function ColecaoNoPerfil({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [c, setC] = useState<Colecao | null>(null);
  useEffect(() => { let vivo = true; void lerColecao(sessao).then((x) => { if (vivo) setC(x); }, () => {}); return () => { vivo = false; }; }, [sessao.id]);
  const n = c?.itens.filter((x) => x.tem).length ?? 0, total = c?.itens.length ?? 0;
  const moldura = c?.itens.find((x) => x.id === c.equipados.moldura);
  return (
    <button className="cartao linha-plano" onClick={() => ir({ tela: 'colecao' })}>
      {moldura ? <Previa x={moldura} sessao={sessao} perfil={perfil} tamanho={44} /> : <span className="emoji-linha" aria-hidden="true">🎨</span>}
      <span><b>Coleção</b><small>{c ? `${n} de ${total} itens · mesa, verso e moldura` : 'Mesa, verso e moldura'}</small></span>
      <Icone nome="seta" tamanho={18} />
    </button>
  );
}
